import b from 'b_';
import isEmpty from 'lodash/isEmpty';
import { Text } from 'platform-components';
import { TEXT_MARKUP_TYPE } from 'platform-components/constants';
import { withRouterPath, type InjectedProps as WithRouterPath } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import ym from 'react-yandex-metrika';
import { bindActionCreators, compose, Dispatch } from 'redux';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { actions as lessonActions } from 'common.components/lesson';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import { getShouldShowModal as getShouldShowLessonModal } from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import * as userActions from 'common.components/user/actions';
import { getInitialSubject } from 'store/subject-config/selectors';
import { getIsTeacherFromMes } from 'utils/external-lms/external-lms';

import type { ReduxTeacherStore, RootAction } from '../../typings';

import Controls from './__controls/lesson-editor-widget__controls';
import DotsMenu from './__dots-menu/lesson-editor-widget__dots-menu';
import Info from './__info/lesson-editor-widget__info';
import { actions as lessonWidgetActions } from './actions';
import { getShouldShowWidget } from './selectors';

import './lesson-editor-widget.scss';

const WIDGET_DELAY = 400;

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface OnwProps extends WithRouterPath {}

interface Props extends OnwProps, StoreProps, DispatchProps {}

interface State {
    delayed: boolean;
}

export class LessonEditorWidget extends React.Component<Props, State> {
    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };

    state = {
        delayed: false,
    };

    componentDidMount() {
        const { startListen } = this.props;

        // чтобы виджет анимировался при переходе на библиотеку
        // eslint-disable-next-line react/no-did-mount-set-state
        if (!this.state.delayed) {
            setTimeout(() => {
                this.setState({ delayed: true });
            }, WIDGET_DELAY);
        }

        startListen();
    }

    componentWillUnmount() {
        const { stopListen } = this.props;

        stopListen();
    }
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    getSubject = () => {
        const {
            initialSubject,
            location: { pathname },
        } = this.props;
        const { staticUrls } = this.context;

        const match = matchPath<{ subject: string }>(pathname, { path: staticUrls.labLibrarySubject, exact: false });

        if (match) {
            const {
                params: { subject },
            } = match;

            return subject;
        }

        return initialSubject;
    };

    handleOpenLessonView = () => {
        const { setWizardVariables, toggleLessonModal, toggleLessonNotification, cLessonId } = this.props;

        toggleLessonModal(true);
        toggleLessonNotification(false);

        ym('reachGoal', 'teacher_lesson_creation_editor', {
            subject: this.getSubject(),
            clessonId: cLessonId,
        });
        setWizardVariables({ widgetButtonClicked: true });
    };

    deleteLesson = () => {
        const { deleteCLesson, cLessonId, showLessonDeletedToast } = this.props;

        if (cLessonId) {
            deleteCLesson({
                cLessonId,
                onSuccess: () => showLessonDeletedToast({ cLessonId, isDraft: true }),
                isDraft: true,
            });

            ym('reachGoal', 'teacher_lesson_creation_editorpreview_delete_draft', {
                clessonId: cLessonId,
            });
        }
    };

    renderWidgetWithProblems = () => {
        const { canEdit, problems, shouldShowWidget, isLessonAssigned, disableDrafts } = this.props;

        // здесь, а не выше, потому что у CSSTransition должны быть children
        if (!shouldShowWidget) {
            return null;
        }

        return (
            <>
                {canEdit && !isLessonAssigned && (
                    <DotsMenu disableDrafts={disableDrafts} onDelete={this.deleteLesson} />
                )}
                <Info numberOfCards={problems.length} subject={this.getSubject()} />
                {!canEdit && (
                    <div className="lesson-editor-widget__not-editable-description">
                        <Text id="lessonEditorWidget.notEditableDescription" type={TEXT_MARKUP_TYPE.HTML} />
                    </div>
                )}
                <Controls
                    onEdit={this.handleOpenLessonView}
                    textId={canEdit ? 'lesson-editor-widget.assign' : 'lessonEditorWidget.button.goToLessonInProgress'}
                />
            </>
        );
    };

    render() {
        const { problems, shouldShowWidget } = this.props;
        const { delayed } = this.state;
        const areProblemsEmpty = isEmpty(problems);
        const classNames = {
            enter: 'lesson-editor-widget__animation_enter',
            enterActive: 'lesson-editor-widget__animation_enter-active',
            exit: 'lesson-editor-widget__animation_exit',
            exitActive: 'lesson-editor-widget__animation_exit-active',
        };

        const notEmptyOpenedWidget = shouldShowWidget && !areProblemsEmpty;

        return (
            <div className={b('lesson-editor-widget', 'wrapper', { active: notEmptyOpenedWidget })}>
                <CSSTransition
                    classNames={classNames}
                    in={notEmptyOpenedWidget && delayed}
                    mountOnEnter={true}
                    timeout={500}
                    unmountOnExit={true}
                >
                    <div className="lesson-editor-widget">{this.renderWidgetWithProblems()}</div>
                </CSSTransition>
            </div>
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: OnwProps) => {
    const {
        match: {
            params: { subject },
        },
        location: { search },
    } = ownProps;

    const shouldShowWidget = getShouldShowWidget(storeState, { subject });
    const shouldShowLessonModal = getShouldShowLessonModal(storeState);

    return {
        canEdit: lessonSelectors.getCanEditLesson(storeState),
        cLessonId: lessonSelectors.getCLessonId(storeState),
        courseId: getCurrentYearCourseId(storeState),
        // Учителю из МЭШ доступен только рубрикатор
        disableDrafts: getIsTeacherFromMes(search),
        isLessonAssigned: lessonSelectors.getIsLessonAssigned(storeState),
        initialSubject: getInitialSubject(storeState),
        problems: lessonSelectors.getLoadedLessonProblems(storeState),
        shouldShowWidget: shouldShowWidget && !shouldShowLessonModal,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) =>
    bindActionCreators(
        {
            startListen: lessonWidgetActions.startListen,
            stopListen: lessonWidgetActions.stopListen,
            deleteCLesson: lessonActions.deleteCLesson,
            setWizardVariables: userActions.setWizardVariables,
            toggleLessonModal: lessonViewActions.toggleModal,
            toggleLessonNotification: lessonViewActions.toggleLessonNotification,
            showLessonDeletedToast: lessonActions.showLessonDeletedToast,
        },
        dispatch
    );

export default compose(withRouterPath, connect(mapStateToProps, mapDispatchToProps))(LessonEditorWidget);
